import { createElement } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { BoardProvider } from '@/features/project-board/BoardProvider';
import { AppShell } from '@/app/AppShell';
import { LoginView } from '@/app/login/LoginView';
import { PasswordSetupView } from '@/app/login/PasswordSetupView';
import { useSession } from '@/app/login/session';
import { NAV_SECTIONS, NavAccess, NavRoute } from '@/app/navigation';
import { EmptyState } from '@/shared/ui/atoms';

/**
 * 라우트는 메뉴 레지스트리에서 파생한다 — 메뉴를 추가하면 라우트가 자동으로 생긴다.
 * 섹션별 데이터 컨텍스트는 그 섹션 라우트에만 감싼다(다른 섹션이 보드 인덱스를 불러오지 않게).
 * 데이터 컨텍스트가 필요 없는 섹션은 여기 등록하지 않으면 된다.
 */
const sectionProviders: Record<string, (children: React.ReactNode) => React.JSX.Element> = {
  'project-board': (children) => <BoardProvider>{children}</BoardProvider>,
};

const sectionRoutes: RouteObject[] = NAV_SECTIONS.flatMap((section) =>
  section.items.map((item) => ({
    path: NavRoute.pathOf(section, item),
    element:
      sectionProviders[section.key]?.(createElement(item.element)) ?? createElement(item.element),
  })),
);

/** 첫 화면 — 계정이 열 수 있는 첫 메뉴로 보낸다(첫 섹션 권한이 없는 계정도 있다). */
function HomeRedirect(): React.JSX.Element {
  const { session } = useSession();
  const target = NavAccess.firstPath(session?.required ? (session.account?.menus ?? []) : null);
  return target ? <Navigate to={target} replace /> : <EmptyState message="열 수 있는 메뉴가 없습니다. 관리자에게 권한을 요청해 주세요." />;
}

export const router = createBrowserRouter([
  // 로그인 화면은 셸 밖이다 — 셸은 로그인하지 않은 사람을 이리로 보낸다.
  { path: '/login', element: <LoginView /> },
  { path: '/login/setup', element: <PasswordSetupView /> },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <HomeRedirect /> },
      ...sectionRoutes,
      { path: '*', element: <HomeRedirect /> },
    ],
  },
]);
